
import { useCallback, useRef } from 'react';
import { photoApi } from '@/lib/api/photo.api';
import { createLogger } from '@/lib/logger';

const logger = createLogger('hooks/useImageUrlRefresh');

interface UseImageUrlRefreshOptions {
  onError?: (error: Error) => void;
}

/**
 * Hook for handling image URL refresh when signed URLs expire
 */
export function useImageUrlRefresh(options: UseImageUrlRefreshOptions = {}) {
  const { onError } = options;
  const refreshingKeys = useRef(new Set<string>());

  /**
   * Handle image load error by refreshing the signed URL
   */
  const handleImageError = useCallback(async (event: Event) => {
    const img = event.target as HTMLImageElement;
    const s3Key = img.getAttribute('data-s3-key');
    
    if (!s3Key || refreshingKeys.current.has(s3Key)) {
      return; // Already refreshing this key
    }

    refreshingKeys.current.add(s3Key);
    
    try {
      const signedUrls = await photoApi.refreshSignedUrls([s3Key]);
      
      if (signedUrls[s3Key]) {
        img.src = signedUrls[s3Key];
      } else {
        logger.warn('[useImageUrlRefresh] No signed URL received for:', s3Key);
      }
    } catch (error) {
      logger.error('[useImageUrlRefresh] Error refreshing URL for', s3Key, ':', error);
      onError?.(error instanceof Error ? error : new Error('Unknown error'));
    } finally {
      refreshingKeys.current.delete(s3Key);
    }
  }, [onError]);

  /**
   * Refresh URLs for multiple images in a container
   */
  const refreshContainerUrls = useCallback(async (container: HTMLElement) => {
    const images = container.querySelectorAll('img[data-s3-key]');
    if (images.length === 0) return;

    const s3Keys: string[] = [];
    images.forEach(img => {
      const key = img.getAttribute('data-s3-key');
      if (key && !refreshingKeys.current.has(key)) {
        s3Keys.push(key);
        refreshingKeys.current.add(key);
      }
    });

    if (s3Keys.length === 0) return;

    try {
      const signedUrls = await photoApi.refreshSignedUrls(s3Keys);
      
      images.forEach(img => {
        const key = img.getAttribute('data-s3-key');
        if (key && signedUrls[key]) {
          img.setAttribute('src', signedUrls[key]);
        }
      });
    } catch (error) {
      logger.error('[useImageUrlRefresh] Error refreshing container URLs:', error);
      onError?.(error instanceof Error ? error : new Error('Unknown error'));
    } finally {
      s3Keys.forEach(key => refreshingKeys.current.delete(key));
    }
  }, [onError]);

  /**
   * Add error event listeners to images in a container
   */
  const addErrorListeners = useCallback((container: HTMLElement) => {
    const images = container.querySelectorAll('img[data-s3-key]');

    images.forEach(img => {
      img.addEventListener('error', handleImageError);

      // Recover images that already failed before this listener was
      // attached (the error event fires during HTML injection, ahead of
      // this effect)
      const imageEl = img as HTMLImageElement;
      if (imageEl.complete && imageEl.naturalWidth === 0 && imageEl.src) {
        void handleImageError({ target: imageEl } as unknown as Event);
      }
    });

    return () => {
      images.forEach(img => {
        img.removeEventListener('error', handleImageError);
      });
    };
  }, [handleImageError]);

  return {
    handleImageError,
    refreshContainerUrls,
    addErrorListeners,
  };
}